import { devMode, env } from "@src/env";
import CryptoJS from "crypto-js";
import { EncryptStorage } from "encrypt-storage";
import { createJSONStorage } from "zustand/middleware";

// v3: `EncryptStorage` virou factory (não se usa mais `new`). `engine: "noble"`
// dá a implementação SÍNCRONA (@noble/ciphers), necessária porque o adapter do
// zustand abaixo é síncrono. (O engine "web-crypto" seria assíncrono.)
export const storage = EncryptStorage.create(env.secretKey, {
  engine: "noble",
  doNotEncryptValues: devMode,
  stateManagementUse: true,
});

// ─── MIGRAÇÃO v2 → v3 (TEMPORÁRIA) ──────────────────────────────────────────
// A v2 cifrava com AES do crypto-js; a v3 usa AES-GCM (@noble) — formatos
// incompatíveis. Sem isto, TODO cliente logado seria deslogado ao auto-atualizar
// para a v3. Estratégia lazy: quando o getItem da v3 falha (valor da v2), decifra
// com a v2, reencripta no formato v3 e devolve — transparente para o cliente.
// Remover este bloco (e a dep `crypto-js`) quando a base já tiver migrado.
function decryptLegacyV2(name: string): string | null {
  // Em dev a v2 não cifrava (doNotEncryptValues) — nada a migrar.
  if (devMode) return null;
  const raw = window.localStorage.getItem(name);
  if (!raw) return null;
  try {
    const value = CryptoJS.AES.decrypt(raw, env.secretKey).toString(
      CryptoJS.enc.Utf8,
    );
    return value || null; // "" = não conseguiu decifrar
  } catch {
    return null; // dado corrompido / não é da v2
  }
}
// ────────────────────────────────────────────────────────────────────────────

/**
 * Lê um valor persistido, migrando de v2 para v3 quando necessário. Usado pelo
 * adapter do zustand e pela varredura de sessões memorizadas (`utils/sessions`).
 */
export function readPersisted(name: string): string | null {
  try {
    return storage.getItem<string>(name) ?? null;
  } catch {
    // v3 não decodificou → provável valor da v2. Migra na hora.
    const legacy = decryptLegacyV2(name);
    if (legacy === null) return null;
    try {
      storage.setItem(name, legacy); // reencripta no formato v3
    } catch {
      // se a reencriptação falhar, ainda devolvemos o valor desta vez
    }
    return legacy;
  }
}

export function getZustandStorage<T = string>() {
  return createJSONStorage<T>(() => ({
    getItem: (name: string) => readPersisted(name),
    setItem: (name: string, value: string) => storage.setItem(name, value),
    removeItem: (name: string) => storage.removeItem(name),
  }));
}
